import { createContext, useContext } from 'react';

import { createFormatters, DEFAULT_LOCALE, type Formatters } from './format';
import { DEFAULT_LANGUAGE, type TextDirection } from './languages';
import { loadedDictionaries } from './loader';
import { createTranslator, type Translator } from './translate';

export type I18nScope = 'admin' | 'storefront';

export interface I18nContextValue {
  /** The language being rendered (the previous one while a new one loads). */
  language: string;
  /** Its `Intl` locale: `hi-IN`. */
  locale: string;
  direction: TextDirection;
  scope: I18nScope;
  /**
   * The wording variants `t` tries before the plain text, most specific
   * first - the store's industry, then its sector. Empty for plain wording.
   */
  variants: readonly string[];
  t: Translator;
  /** Ask for another language. Rendering switches once its dictionaries load. */
  setLanguage: (code: string) => void;
  /** False while the requested language's dictionaries are still loading. */
  ready: boolean;
  /** Number/money/date/list formatters bound to `locale`. */
  formatters: Formatters;
}

/**
 * English, synchronously - what every component gets with no provider above
 * it, so existing tests, embeds and isolated renders keep working unchanged.
 */
export const DEFAULT_I18N: I18nContextValue = {
  language: DEFAULT_LANGUAGE,
  locale: DEFAULT_LOCALE,
  direction: 'ltr',
  scope: 'admin',
  variants: [],
  t: createTranslator(loadedDictionaries(), [DEFAULT_LANGUAGE]),
  setLanguage: () => {},
  ready: true,
  formatters: createFormatters(DEFAULT_LOCALE),
};

export const I18nContext = createContext<I18nContextValue | null>(null);

export function useI18n(): I18nContextValue {
  return useContext(I18nContext) ?? DEFAULT_I18N;
}

/** The translator alone: `const t = useT(); t('common.save')`. */
export function useT(): Translator {
  return useI18n().t;
}

export function useFormatters(): Formatters {
  return useI18n().formatters;
}
